import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { getBinClampedSize } from '@common-util/texture-util';
import { eTextureChannel } from '@models/classes';
import { DisplacementMapFeature } from '@models/classes/features/displacement-map-feature';
import { Token } from '@models/classes/token';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { FeatureRenderer } from './feature';
import { ensureNormalsExist } from './utils';

export class DisplacementMapRenderer extends FeatureRenderer<DisplacementMapFeature> {
    _initialized: boolean;
    modelToken = Token.DisplacementMapFeature;
    textureCanvas = document.createElement('canvas');

    updateMeshGeometry(
        feature: DisplacementMapFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);
        const textureRenderer = this.viewer.rendererManager.textures;
        if (feature.textures && feature.textures.length > 0) {
            //create context
            const context = this.textureCanvas.getContext('2d', {
                willReadFrequently: true,
            });
            const strength = 1;
            const hasTransparency = false;
            
            const textureLayers = textureRenderer.getActiveLayers(feature, eTextureChannel.albedo).filter(node => {
                const renderer = textureRenderer.getRenderer(node);
                return renderer.textureHasContent(node);
            });

            if (context) {
                const baseImageSize = textureRenderer
                    .getRenderer(feature.textures[0])
                    .getNativeSize(feature.textures[0]);
                const parameters = { strength, hasTransparency, nativeSize: baseImageSize };
                
                if (this.scene.getEngine().webGLVersion < 2) {
                    const clampedSize = getBinClampedSize(baseImageSize.width, baseImageSize.height);
                    context.canvas.width = clampedSize;
                    context.canvas.height = clampedSize;
                } else {
                    if (feature.textures[0]) {
                        context.canvas.width = baseImageSize.width;
                        context.canvas.height = baseImageSize.height;
                    }
                }
                context.clearRect(0, 0, context.canvas.width, context.canvas.height);

                const paintedImage = textureRenderer.paintTextureOnCanvas(textureLayers, context, parameters);

                const tempVertexData = new SubmeshVertexData();
                tempVertexData.copyFrom(newVertexData);
                if (tempVertexData.uvs) {
                    const n = tempVertexData.uvs.length;
                    for (let i = 1; i < n; i += 2) {
                        tempVertexData.uvs[i] = 1 - tempVertexData.uvs[i];
                    }

                    const mesh = this.viewer.getRendererNode(feature._parent!);
                    const meshUpdated = new Mesh('ExistingMesh');
                    tempVertexData.applyToMesh(meshUpdated, true);
                    ensureNormalsExist(meshUpdated);

                    const imageData = paintedImage?.getImageData(
                        0,
                        0,
                        this.textureCanvas.width,
                        this.textureCanvas.height,
                        mesh
                    );
                    if (imageData) {
                        this.displace(imageData, feature.minDisplacement, feature.maxDisplacement, meshUpdated);
                    }
                    const meshVertexData = VertexData.ExtractFromMesh(meshUpdated);
                    newVertexData.positions = meshVertexData.positions;
                    newVertexData.normals = meshVertexData.normals;
                    meshUpdated.dispose();
                }
            }
        }
        return newVertexData;
    }

    update3dInternal() {}

    displace(context: ImageData, minDisplacement: number, maxDisplacement: number, parentMesh: Mesh) {
        const uInt8Array = new Uint8Array(context.data);
        parentMesh.applyDisplacementMapFromBuffer(
            uInt8Array,
            context.width,
            context.height,
            minDisplacement,
            maxDisplacement
        );
    }
}
